/**
 * Interface representing the structure of the ETMC data used for tracking.
 * This interface defines the required and optional fields needed for initializing
 * tracking in the ExactTarget Marketing Cloud (ETMC).
 */
export interface EtmcData {
	/** The organization ID used by ExactTarget Marketing Cloud. */
	exactTargetOrgId?: string;

	/** Indicates whether the user is known (true) or not (false). */
	userKnown?: boolean;

	/** The email address of the user, if known. */
	customerEmail?: string;

	/** The name of the current page being viewed. */
	pageName?: string;

	/** The product code, if the page is a product-related page. */
	productCode?: string;

	/** The product category, if the page is a product list page. */
	productCategory?: string;
}

/**
 * Interface representing the structure for user information.
 * This is used to store the user's email address for tracking purposes.
 */
export interface UserInfo {
	/** The email address of the user. */
	email: string;
}

/**
 * Interface representing the parameters for tracking page views.
 * These parameters are used when tracking specific actions on different pages.
 */
export interface TrackPageViewParams {
	/** The item being viewed on a product-related page. */
	item?: string;

	/** The search term used, if the page is a search results page. */
	search?: string;

	/** The category of products being viewed, if the page is a product list page. */
	category?: string;
}

/**
 * Type representing all possible commands that can be pushed to the ExactTarget Marketing Cloud (ETMC) command array.
 * This type is a union of different command formats, each corresponding to a specific action tracked by ETMC.
 */
export type EtmcCommand =
	| ['setOrgId', string]
	| ['setUserInfo', UserInfo]
	| ['trackPageView', TrackPageViewParams];

/**
 * Extends the global Window interface to include the _etmc array.
 * The _etmc array stores commands to be sent to the ExactTarget Marketing Cloud (ETMC).
 */
declare global {
	interface Window {
		/** Global array to store commands for the ExactTarget Marketing Cloud (ETMC). */
		_etmc: EtmcCommand[];
	}
}

/**
 * Function to initialize ETMC tracking with provided ETMC data.
 * This function populates the global `_etmc` array with tracking commands
 * based on the data provided about the user's current session and the page being viewed.
 *
 * @param {EtmcData} etmcData - The ETMC data containing necessary information for tracking.
 * @returns {void} This function does not return a value.
 */
export function initializeEtmcTracking(etmcData: EtmcData): void {
	// Initialize the global _etmc array if it doesn't already exist.
	if (!window._etmc) {
		window._etmc = [];
	}

	// Adds the organization ID to the global _etmc array, if it exists.
	if (etmcData.exactTargetOrgId) {
		window._etmc.push(['setOrgId', etmcData.exactTargetOrgId]);
	}

	// Checks if the user is known and adds their information to the global _etmc array if so.
	if (etmcData.userKnown && etmcData.customerEmail) {
		// Ensure customerEmail is not undefined
		window._etmc.push(['setUserInfo', { email: etmcData.customerEmail }]);
	}

	// Determine the current page name from the provided data, if it exists.
	const pageName: string | undefined = etmcData.pageName;

	// Track the page view based on the page type, if pageName is defined.
	if (pageName === 'product' || pageName === 'giftsProduct' || pageName === 'productMixed') {
		const productCode: string | undefined = etmcData.productCode;
		if (productCode) {
			window._etmc.push(['trackPageView', { item: productCode }]);
		}
	} else if (pageName === 'productList') {
		const currentUrl: string = window.location.href;

		// Check if the URL includes a search term and track it accordingly.
		if (currentUrl.includes('search')) {
			const searchTerm: string = currentUrl.split('search').pop() || '';
			window._etmc.push([
				'trackPageView',
				{ search: decodeURIComponent(searchTerm).replace(/^\/+/, '') },
			]);
		} else if (etmcData.productCategory) {
			// If the page is a product list page but not a search results page, track the category.
			window._etmc.push(['trackPageView', { category: etmcData.productCategory }]);
		}
	}
}
